import React from "react";
import {
  Flex,
  IconButton,
  Button,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisible?: number;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  maxVisible = 10,
}) => {
  const { colorMode } = useColorMode();

  const activeBg = useColorModeValue("brand.500", "brand.400");
  const activeColor = useColorModeValue("white", "white");
  const inactiveBg = useColorModeValue("light.100", "dark.300");
  const inactiveColor = useColorModeValue("dark.500", "light.100");
  const borderColor = useColorModeValue("gray.300", "gray.600");

  // Calculate visible page numbers
  const getVisiblePages = (): number[] => {
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const halfVisible = Math.floor(maxVisible / 2);
    let start = Math.max(1, currentPage - halfVisible);
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();

  return (
    <Flex
      position="sticky"
      bottom={0}
      bg={`linear-gradient(0deg, ${
        colorMode === "dark" ? "#17243d" : "#ffffff"
      }, transparent)`}
      p={4}
      justify="center"
      align="center"
      gap={2}
      zIndex={10}
    >
      {/* Previous Arrow */}
      <IconButton
        aria-label="صفحه قبلی"
        icon={<ChevronRightIcon />}
        onClick={() => onPageChange(currentPage - 1)}
        isDisabled={currentPage === 1}
        size="sm"
        variant="ghost"
        borderRadius="full"
        _hover={{ bg: inactiveBg }}
      />

      {/* Page Numbers */}
      {visiblePages.map((page) => {
        const isActive = currentPage === page;
        return (
          <Button
            key={page}
            onClick={() => onPageChange(page)}
            size="sm"
            minW="40px"
            h="40px"
            borderRadius="full"
            borderWidth={"1px"}
            borderColor={isActive ? activeBg : borderColor}
            bg={isActive ? activeBg : inactiveBg}
            color={isActive ? activeColor : inactiveColor}
            _hover={{
              bg: activeBg,
              color: activeColor,
              borderColor: activeBg,
              opacity: 0.8,
            }}
            fontWeight={isActive ? "bold" : "medium"}
          >
            {page}
          </Button>
        );
      })}

      {/* Next Arrow */}
      <IconButton
        aria-label="صفحه بعدی"
        icon={<ChevronLeftIcon />}
        onClick={() => onPageChange(currentPage + 1)}
        isDisabled={currentPage === totalPages}
        size="sm"
        variant="ghost"
        borderRadius="full"
        _hover={{ bg: inactiveBg }}
      />
    </Flex>
  );
};
